// Multi-study data layer. Uses Supabase when configured; otherwise a localStorage
// demo fallback. Every row is tagged with study_slug so one project hosts many
// studies. The knowledge answer key is NEVER here — scoring is in the `score` Edge Function.
import { supabase, isConfigured } from './supabase';

const LS_KEY = 'kap_local_v1';
const loadLocal = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; } };
const saveLocal = (d) => localStorage.setItem(LS_KEY, JSON.stringify(d));

export { isConfigured };

// Postgres error helpers so the data layer tolerates migration 0005 not being
// applied yet (missing column) and idempotent re-submits (unique violation).
const isUniqueViolation = (e) => !!e && (e.code === '23505' || /duplicate key|unique constraint/i.test(String(e?.message || '')));
const isMissingColumn = (e) => !!e && (e.code === '42703' || /instrument_version.*does not exist|column .*instrument_version/i.test(String(e?.message || '')));

export async function ensureSession() {
  if (isConfigured) {
    let { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      const { data, error } = await supabase.auth.signInAnonymously();
      if (error) throw error;
      session = data.session;
    }
    return session.user.id;
  }
  const d = loadLocal();
  if (!d.uid) { d.uid = crypto.randomUUID(); saveLocal(d); }
  return d.uid;
}

// Columns that actually exist on public.participant. Study-specific demographics
// (e.g. tobaccoUse, hepBVaccine, examTraining) are dropped here — they're still
// captured in the `response` table (item A4…) and surfaced in the export.
const PARTICIPANT_COLS = ['cohort', 'age', 'sex', 'prior_training', 'workshop', 'info_source', 'group_tag'];

export async function saveParticipant(uid, studySlug, fields) {
  const clean = {};
  for (const k of PARTICIPANT_COLS) if (fields[k] !== undefined && fields[k] !== '') clean[k] = fields[k];
  if (isConfigured) {
    const { error } = await supabase.from('participant')
      .upsert({ user_id: uid, study_slug: studySlug, ...clean, consent_at: new Date().toISOString() },
        { onConflict: 'user_id,study_slug' });
    if (error) throw error;
    return;
  }
  const d = loadLocal();
  d.participant = (d.participant || []).filter((p) => !(p.user_id === uid && p.study_slug === studySlug));
  d.participant.push({ user_id: uid, study_slug: studySlug, ...clean });
  saveLocal(d);
}

async function saveResponses(uid, studySlug, phase, answers) {
  const rows = Object.entries(answers).map(([item_key, value]) => ({
    user_id: uid, study_slug: studySlug, phase, item_key, value: value == null ? null : String(value),
  }));
  if (isConfigured) {
    const { error } = await supabase.from('response').insert(rows);
    if (error && !isUniqueViolation(error)) throw error; // re-submit is idempotent
    return;
  }
  const d = loadLocal();
  d.responses = (d.responses || []).concat(rows);
  saveLocal(d);
}

// Saves raw responses, then asks the Edge Function to score + record the submission
// (so the answer key stays server-side). Returns scores or null.
export async function submitPhase(uid, studySlug, phase, answers, durationSeconds, instrumentVersion) {
  await saveResponses(uid, studySlug, phase, answers);
  if (isConfigured) {
    try {
      const { data, error } = await supabase.functions.invoke('score', {
        body: { study_slug: studySlug, phase, answers, durationSeconds, instrument_version: instrumentVersion ?? null },
      });
      if (error) throw error;
      return data;
    } catch (e) {
      console.warn('score function unavailable; recording a completion row without scores.', e);
      const base = { user_id: uid, study_slug: studySlug, phase, duration_seconds: durationSeconds ?? null };
      let { error: e2 } = await supabase.from('submission').insert({ ...base, instrument_version: instrumentVersion ?? null });
      if (e2 && isMissingColumn(e2)) ({ error: e2 } = await supabase.from('submission').insert(base));
      if (e2 && !isUniqueViolation(e2)) console.warn('fallback submission insert failed', e2);
      return null;
    }
  }
  const d = loadLocal();
  d.submissions = (d.submissions || []).concat([{ user_id: uid, study_slug: studySlug, phase, durationSeconds }]);
  saveLocal(d);
  return null;
}

export async function logEducation(uid, studySlug, moduleId, timeSpentSeconds) {
  if (isConfigured) {
    const { error } = await supabase.from('education_event').insert({
      user_id: uid, study_slug: studySlug, module_id: moduleId,
      completed_at: new Date().toISOString(), time_spent_seconds: timeSpentSeconds,
    });
    if (error) console.warn('education_event insert failed', error);
    return;
  }
  const d = loadLocal();
  d.education = (d.education || []).concat([{ user_id: uid, study_slug: studySlug, module_id: moduleId, time_spent_seconds: timeSpentSeconds }]);
  saveLocal(d);
}

// Opt-in mailing list for guideline updates + new studies. DELIBERATELY a separate
// table with NO user_id / response link, so the email can never be tied to anyone's
// anonymous survey answers. Needs migration 0006.
export async function subscribe(email, source, profile) {
  if (isConfigured) {
    const base = { email, source: source || null };
    const full = { ...base, role: profile?.role || null, field: profile?.field || null, study_year: profile?.study_year || null, experience: profile?.experience || null };
    let { error } = await supabase.from('subscriber').insert(full);
    if (error && (error.code === '42703' || /column .*does not exist/i.test(String(error.message || '')))) {
      ({ error } = await supabase.from('subscriber').insert(base)); // 0008 not applied yet — store email only
    }
    if (error && !isUniqueViolation(error)) throw error; // already subscribed is fine
    return;
  }
  const d = loadLocal();
  d.subscribers = (d.subscribers || []).concat([{ email, source, ...profile }]);
  saveLocal(d);
}

// Anonymous completion feedback (a "how beneficial was this?" metric). Linked only
// to the random session id, never PII. Upsert = one rating per person per study.
// Needs migration 0007.
export async function saveFeedback(uid, studySlug, { usefulness, learned }) {
  if (isConfigured) {
    const { error } = await supabase.from('feedback')
      .upsert({ user_id: uid, study_slug: studySlug, usefulness, learned: learned || null }, { onConflict: 'user_id,study_slug' });
    if (error) throw error;
    return;
  }
  const d = loadLocal();
  d.feedback = (d.feedback || []).concat([{ user_id: uid, study_slug: studySlug, usefulness, learned }]);
  saveLocal(d);
}
